import { Button, Box } from '@mui/material';
import { blue } from '@mui/material/colors';
import DiffIcon from '@mui/icons-material/Difference';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import type { FC } from 'react';
import FormattedTooltip from './FormattedTooltip';
import { calculateCER } from '@/utils/note';
import { cleanText } from '@/utils/transcriptReplacements';

interface DiffToggleButtonProps {
  mdxDiffEnabled: boolean;
  toggleMdxDiff: () => void;
  transcript: string | undefined;
  value: string;
}

const DiffToggleButton: FC<DiffToggleButtonProps> = ({
  mdxDiffEnabled,
  toggleMdxDiff,
  transcript,
  value,
}) => {
  const diffSize = getRoughDiffSize(transcript || '', cleanText(value));

  return (
    <FormattedTooltip
      title={diffSize.description}
      description={`Viser forskelle mellem transskript og redigerede noter.`}
    >
      <Button
        variant={mdxDiffEnabled ? 'outlined' : 'contained'}
        color="secondary"
        onClick={toggleMdxDiff}
        sx={{
          color: 'white',
          mx: 0.5,
          px: 1,
          minWidth: '32px',
          backgroundColor: mdxDiffEnabled ? 'white' : undefined,
          '& svg': {
            color: mdxDiffEnabled ? `${blue[500]} !important` : 'white !important',
          },
        }}
      >
        {mdxDiffEnabled ? <TextFieldsIcon fontSize="small" /> : <DiffIcon fontSize="small" />}
        {/* based on the diff size, show a small indicator of levels 1-3 with the three white blocks stacked vertically*/}
        <DiffSizeIndicator scale={diffSize.scale} mdxDiffEnabled={mdxDiffEnabled} />
      </Button>
    </FormattedTooltip>
  );
};

const DiffSizeIndicator: FC<{ scale: number; mdxDiffEnabled: boolean }> = ({
  scale,
  mdxDiffEnabled,
}) => {
  const thresholds = [2, 1, 0];
  const baseBoxStyles = {
    width: '6px',
    height: '3px',
    backgroundColor: mdxDiffEnabled ? blue[500] : 'white',
  };

  return (
    <Box display="flex" flexDirection="column" gap={0.25} ml={0.5}>
      {thresholds.map((threshold) => (
        <Box key={threshold} sx={{ ...baseBoxStyles, opacity: scale > threshold ? 1 : 0.3 }} />
      ))}
    </Box>
  );
};

const getRoughDiffSize = (
  reference: string,
  hypothesis: string,
): { scale: number; description: string } => {
  const characterErrorRate = calculateCER(reference, hypothesis);
  if (characterErrorRate === 0) {
    return { scale: 0, description: 'Ingen ændringer foretaget.' };
  } else if (characterErrorRate < 0.2) {
    return { scale: 1, description: 'Kun få ændringer foretaget.' };
  } else if (characterErrorRate < 0.4) {
    return { scale: 2, description: 'Nogle ændringer foretaget.' };
  } else {
    return { scale: 3, description: 'Mange ændringer foretaget.' };
  }
};

export default DiffToggleButton;
